import { Injectable, inject, signal } from '@angular/core';
import { AdminService } from './admin.service';

/**
 * Caches the permission catalog as a `{ slug -> description }` map so a 403
 * (`Missing permission: <slug>`) can be shown to the user with a human-readable
 * label instead of the raw slug.
 *
 * Loaded once at login (`LoggedUserEffects`) via `GET /permissions` — an endpoint
 * any authenticated user can read (no `@RequiresPermission`). Best-effort: any
 * load error is swallowed, and `describe()` falls back to the slug, so a missing
 * or not-yet-loaded catalog never breaks the error toast.
 *
 * Signal-based (zoneless-safe). The UI never reads it directly; only the
 * `errorInterceptor` calls `describe()` synchronously when building the toast.
 */
@Injectable({ providedIn: 'root' })
export class PermissionCatalogService {
  private readonly adminService = inject(AdminService);

  /** `permission.name` (slug) -> `permission.description`. */
  private readonly _labels = signal<Map<string, string>>(new Map());

  /** Fetch the catalog and cache the slug->description map. Best-effort. */
  load(): void {
    this.adminService.getPermissions().subscribe({
      next: (permissions) => {
        const map = new Map<string, string>();
        for (const p of permissions) {
          const description = p.description?.trim();
          if (description) map.set(p.name, description);
        }
        this._labels.set(map);
      },
      error: () => {
        // Best-effort — the slug fallback in describe() covers a failed load.
      },
    });
  }

  /** Human-readable label for a permission slug, or the slug itself if unknown. */
  describe(slug: string): string {
    return this._labels().get(slug) ?? slug;
  }

  /** Reset the cache (on logout). */
  clear(): void {
    this._labels.set(new Map());
  }
}
